import { z } from 'zod';
/**
 * Handing the Android app to field agents over WhatsApp: an admin uploads the
 * APK here, the server keeps the file (the only per-build file it stores) and
 * publishes an unauthenticated download link plus a QR code.
 */
/**
 * The field app's Android application id and iOS bundle identifier (D80). It
 * cannot change after the first store upload: Android treats another id as
 * another app. apps/mobile/app.config.ts repeats the literal (the config loader
 * cannot import this package); a test keeps the two equal.
 */
export const MOBILE_APP_ID = 'com.loynova.visits';
/** Absolute ceiling; the server enforces `MAX_APK_BYTES` (default 150 MB) on top of this. */
export const MAX_APK_BYTES = 300 * 1024 * 1024;
export const APK_CONTENT_TYPE = 'application/vnd.android.package-archive';
/** Older builds past this count are deleted when a new one is published. */
export const APK_BUILDS_KEPT = 2;
export const APK_DOWNLOAD_TOKEN_BYTES = 24;
/** Base64url of 24 random bytes. */
export const APK_DOWNLOAD_TOKEN_PATTERN = /^[A-Za-z0-9_-]{32,64}$/;
export const appVersionSchema = z
    .string()
    .trim()
    .regex(/^\d+\.\d+\.\d+([-+][0-9A-Za-z.-]+)?$/, 'Use semantic versioning, e.g. 1.4.0');
/**
 * Compares two `major.minor.patch` versions, ignoring any pre-release/build
 * suffix. Returns < 0, 0 or > 0. Anything unparseable sorts as 0.0.0.
 */
export function compareAppVersions(a, b) {
    const parts = (v) => {
        const core = v.trim().split(/[-+]/)[0] ?? '';
        const [major = 0, minor = 0, patch = 0] = core.split('.').map((n) => {
            const parsed = Number(n);
            return Number.isFinite(parsed) ? parsed : 0;
        });
        return [major, minor, patch];
    };
    const left = parts(a);
    const right = parts(b);
    for (let i = 0; i < 3; i++) {
        if ((left[i] ?? 0) !== (right[i] ?? 0))
            return (left[i] ?? 0) - (right[i] ?? 0);
    }
    return 0;
}
/**
 * What the server APK builder can produce (D103). The profile sets APP_ENV for
 * app.config.ts and decides the Gradle variant:
 *   preview / production: `assembleRelease`, the JavaScript bundled inside, runs on its own.
 *   development: `assembleDebug`, the expo-dev-client launcher, no bundled JavaScript:
 *                it needs a Metro server on a developer's machine. Never published to agents.
 */
// The server-side APK builder is gone (SECURITY_REVIEW / revamp A4); these profile
// constants remain only for the old RN app's build config and retire with apps/mobile.
export const MOBILE_BUILD_PROFILES = ['preview', 'production', 'development'];
export const mobileBuildProfileSchema = z.enum(MOBILE_BUILD_PROFILES);
export const DEFAULT_BUILD_PROFILE = 'preview';
export const BUILD_PROFILE_TRAITS = {
    preview: { gradleTask: 'assembleRelease', variant: 'release', standalone: true, publishable: true },
    production: { gradleTask: 'assembleRelease', variant: 'release', standalone: true, publishable: true },
    development: { gradleTask: 'assembleDebug', variant: 'debug', standalone: false, publishable: false },
};
/** 10.0.0.0/8, 172.16.0.0/12 and 192.168.0.0/16. */
export function isPrivateIPv4(host) {
    const match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
    if (!match)
        return false;
    const [a, b, c, d] = match.slice(1).map(Number);
    if ([a, b, c, d].some((n) => n > 255))
        return false;
    return a === 10 || (a === 192 && b === 168) || (a === 172 && b >= 16 && b <= 31);
}
const DEV_MACHINE_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]', '10.0.2.2', '10.0.3.2', '0.0.0.0']);
/**
 * The API-address half of `networkPolicy` in apps/mobile/app.config.ts (which
 * cannot import this package): the server checks it before a build starts, so
 * a refused address fails in a second instead of minutes into Gradle. A mobile
 * test runs both over the same cases and expects the same verdict.
 * Returns the reason the address is refused, or null.
 */
export function mobileApiUrlProblem(profile, apiUrl) {
    if (!apiUrl)
        return profile === 'development' ? null : `EXPO_PUBLIC_API_URL is not set; a ${profile} build needs the API address`;
    // This package has no DOM or Node types, so no `URL`: an explicit origin parser instead.
    const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(apiUrl)?.[1]?.toLowerCase();
    if (scheme && scheme !== 'http' && scheme !== 'https')
        return `EXPO_PUBLIC_API_URL must start with https:// ("${apiUrl}")`;
    const match = /^(https?):\/\/(\[[0-9a-f:.]+\]|[^/:?#@\s[\]]+)(?::(\d{1,5}))?(?:[/?#]\S*)?$/i.exec(apiUrl);
    if (!match || (match[3] !== undefined && Number(match[3]) > 65535))
        return `EXPO_PUBLIC_API_URL is not a valid URL ("${apiUrl}")`;
    const protocol = match[1].toLowerCase();
    const host = match[2].toLowerCase();
    const devMachine = DEV_MACHINE_HOSTS.has(host) || host.endsWith('.local');
    if (profile !== 'development' && devMachine) {
        return `EXPO_PUBLIC_API_URL points at a developer machine alias ("${host}"); a ${profile} build runs on real phones`;
    }
    if (profile !== 'development' && !host.includes('.'))
        return `EXPO_PUBLIC_API_URL must be a real domain or IP ("${host}" has no dot)`;
    if (protocol === 'https')
        return null;
    if (profile === 'production')
        return `EXPO_PUBLIC_API_URL must use https:// for a production build (got "http://")`;
    if (isPrivateIPv4(host) || (profile === 'development' && DEV_MACHINE_HOSTS.has(host)))
        return null;
    return `EXPO_PUBLIC_API_URL uses plain http to "${host}"; http is allowed only to a private LAN IP (10.x, 172.16-31.x, 192.168.x). Use https for anything else`;
}
/** What the upload check found inside the APK before deciding whether to accept it. */
export const apkInspectionSchema = z.object({
    entryCount: z.number().int(),
    /** `assets/index.android.bundle`: the app's JavaScript, compiled into the APK. */
    hasJsBundle: z.boolean(),
    jsBundleBytes: z.number().int().nullable(),
    /** expo-dev-client's launcher classes in the dex files: the APK would wait for Metro. */
    hasDevClient: z.boolean(),
    /** `extra.apiUrl` from the embedded `assets/app.config`; what the app will call. */
    embeddedApiUrl: z.string().nullable(),
    embeddedAppEnv: z.string().nullable(),
    /** `extra.eas.projectId` is set: without it the app can never get a push token (D104). A warning, not a failure. */
    hasPushProjectId: z.boolean(),
    /** The id itself (D108): the builder refuses an APK that lost the one it was given. */
    embeddedEasProjectId: z.string().nullable(),
});
export const appBuildDtoSchema = z.object({
    id: z.string(),
    version: z.string(),
    releaseNotes: z.string().nullable(),
    /** Set by the server builder; null for a manual upload or a build made before D103. */
    buildProfile: mobileBuildProfileSchema.nullable(),
    sizeBytes: z.number().int(),
    sha256: z.string(),
    fileName: z.string(),
    isPublished: z.boolean(),
    /** Null once the token is revoked: the public links stop working. */
    downloadUrl: z.string().nullable(),
    landingUrl: z.string().nullable(),
    uploadedAt: z.string(),
    uploadedBy: z.object({ id: z.string(), fullName: z.string() }).nullable(),
});
export const appDistributionDtoSchema = z.object({
    builds: z.array(appBuildDtoSchema),
    /** The build agents are pointed at, if any. */
    published: appBuildDtoSchema.nullable(),
    /** Below this, the app refuses to submit. Null = no floor. */
    minimumSupportedVersion: z.string().nullable(),
    /** Secondary option: Play / cloud drive link, used when no APK is hosted. */
    externalInstallUrl: z.string().nullable(),
    /** The iPhone app's install link (TestFlight); shown on the landing page when set. */
    externalIosInstallUrl: z.string().nullable(),
    /** So the panel can warn about bandwidth before agent counts grow. */
    maxApkBytes: z.number().int(),
    buildsKept: z.number().int(),
    /** App versions seen in submissions; no new tracking, derived from the audit log. */
    versionsInUse: z.array(z.object({
        appVersion: z.string().nullable(),
        visitCount: z.number().int(),
        agentCount: z.number().int(),
        lastSeenAt: z.string(),
    })),
});
export const updateAppDistributionRequestSchema = z.object({
    minimumSupportedVersion: appVersionSchema.nullable(),
    externalInstallUrl: z
        .url({ protocol: /^https$/, message: 'Must be an https URL' })
        .max(500)
        .nullable(),
    externalIosInstallUrl: z
        .url({ protocol: /^https$/, message: 'Must be an https URL' })
        .max(500)
        .nullable(),
});
/**
 * What the app itself asks for on login and on a manual check. Cheap, cacheable
 * and public — so a hosted build's tokened links are never in it (anyone could
 * read them here, which made rotating the token pointless). The landing link is
 * handed to agents out of band; only the intentionally public external links ride along.
 */
export const appVersionInfoSchema = z.object({
    latestVersion: z.string().nullable(),
    minimumSupportedVersion: z.string().nullable(),
    releaseNotes: z.string().nullable(),
    /** The external install link (Play / cloud drive), when distribution is external; never the hosted APK's tokened URL. */
    downloadUrl: z.string().nullable(),
    /** Kept for old clients; always null now that the tokened landing link is not served publicly. */
    landingUrl: z.string().nullable(),
    /** The iPhone app's install link (TestFlight), when set. */
    iosInstallUrl: z.string().nullable(),
    publishedAt: z.string().nullable(),
});
/**
 * Decides what the app shows. `BLOCKED` stops submission; `UPDATE_AVAILABLE` is
 * a notice only. An unknown or unparseable own version is never blocking.
 */
export function evaluateAppVersion(currentVersion, info) {
    if (!currentVersion)
        return 'UP_TO_DATE';
    if (info.minimumSupportedVersion && compareAppVersions(currentVersion, info.minimumSupportedVersion) < 0)
        return 'BLOCKED';
    if (info.latestVersion && compareAppVersions(currentVersion, info.latestVersion) < 0)
        return 'UPDATE_AVAILABLE';
    return 'UP_TO_DATE';
}
// ---------------------------------------------------------------------------
// Reason codes (additive; the English `message` / `error` text is unchanged)
// ---------------------------------------------------------------------------
/**
 * Why an APK upload or publish was refused: `error.details.reason`. Sent with
 * VALIDATION_ERROR, except APK_TOO_LARGE (PAYLOAD_TOO_LARGE, with
 * `details.maxBytes`). A bad `version` field stays a VALIDATION_ERROR with
 * `details.issues` and no reason.
 */
export const APK_UPLOAD_REASONS = [
    /** The request was not multipart/form-data. */
    'APK_NOT_MULTIPART',
    /** No `file` part (or it came before the `version` field). */
    'APK_FILE_MISSING',
    /** Too small to be an APK. */
    'APK_TRUNCATED',
    /** Does not start with a ZIP header. */
    'APK_NOT_A_ZIP',
    /** Incomplete or corrupted: no ZIP central directory. */
    'APK_NO_CENTRAL_DIRECTORY',
    /** A ZIP, but no AndroidManifest.xml entry. */
    'APK_NO_ANDROID_MANIFEST',
    /** Over the server's MAX_APK_BYTES (PAYLOAD_TOO_LARGE, details.maxBytes). */
    'APK_TOO_LARGE',
    /** Publishing a development (Metro-dependent) build. */
    'APK_DEV_BUILD_NOT_PUBLISHABLE',
];
export const apkUploadReasonSchema = z.enum(APK_UPLOAD_REASONS);
/** Why an API address check failed: `reasonCode` of the check-endpoint result. */
export const ENDPOINT_CHECK_REASONS = [
    'INVALID_URL',
    /** Not http:// or https://. */
    'UNSUPPORTED_PROTOCOL',
    /** user:password@ in the address. */
    'CREDENTIALS_IN_URL',
    /** Resolves to a link-local, unspecified, multicast or (in production) loopback address. */
    'ADDRESS_NOT_ALLOWED',
    /** The host name could not be resolved. */
    'DNS_FAILED',
    /** No response within the probe timeout. */
    'TIMEOUT',
    /** The host actively refused the connection (nothing listening on that port). */
    'CONNECTION_REFUSED',
    /** The TLS handshake failed (certificate expired, self-signed, wrong host, ...). */
    'TLS_ERROR',
    /** Any other network failure. */
    'UNREACHABLE',
    /** The health route answered with a redirect (3xx); redirects are not followed. */
    'HTTP_REDIRECT',
    /** The health route answered with a non-2xx status (`statusCode`). */
    'HTTP_ERROR',
];
export const endpointCheckReasonSchema = z.enum(ENDPOINT_CHECK_REASONS);
/** POST /engine/app-distribution/check-endpoint. `error` is English technical detail; translate `reasonCode`. */
export const endpointCheckResultSchema = z.object({
    ok: z.boolean(),
    statusCode: z.number().int().optional(),
    responseTimeMs: z.number().optional(),
    error: z.string().optional(),
    /** Set whenever `ok` is false. Optional: older servers do not send it. */
    reasonCode: endpointCheckReasonSchema.optional(),
});
//# sourceMappingURL=app-distribution.js.map